import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Eye, FileText, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { BenefitPlanTable, benefitCeilingText, effectiveRate } from "../components/BenefitPlanTable";
import { fetchAdminInsurers, type AdminInsurer, type NetworkHospital } from "../api/insurerApi";
import {
  fetchAllPackages,
  fetchClaimsNetwork,
  fetchClaimsPackages,
  saveClaimsPackage,
  setPackageHospitals,
  type InsurancePackage,
  type PackageBenefit,
  type PackagePayload,
  type PackageSection,
  type PackageSummary,
  type PackagesMeta,
} from "../api/claimsApi";
import { getStoredAccountType } from "../auth";
import { getWorkContext, setWorkContext } from "../workContext";

const buttonClass =
  "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60";
const ghostClass =
  "inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-60";

type Draft = PackagePayload & { id?: number };

/** Ligne de la liste : un package, avec son assureur. */
type ListRow = Pick<PackageSummary, "id" | "code" | "name" | "status" | "coverage_zone" | "insurer_name" | "acts_count" | "members_count"> & {
  insurer_id: number | null;
};

const EMPTY_DRAFT: Draft = {
  code: "",
  name: "",
  description: "",
  status: "active",
  currency: "USD",
  coverage_zone: "RDC",
  person_ceiling: null,
  family_ceiling: null,
  sections: [],
};

const NEW_SECTION: PackageSection = {
  name: "",
  ceiling_amount: null,
  ceiling_period: "year",
  ceiling_period_years: null,
  coverage_rate: 100,
  excluded_icd_codes: null,
  hospital_ids: [],
  waiting_months: null,
  note: null,
  benefits: [],
};

const NEW_BENEFIT: PackageBenefit = {
  label: "",
  act_codes: [],
  icd_codes: null,
  coverage_rate: null,
  unit_ceiling: null,
  unit_basis: "act",
  period_ceiling: null,
  max_quantity: null,
  period: "year",
  period_years: null,
  prior_approval: "none",
  prior_approval_threshold: null,
  age_min: null,
  age_max: null,
  note: null,
};

/** Complète les sections venues d'un modèle ou d'une ancienne version (champs du lot 2). */
function withDefaults(plan: PackagePayload): PackagePayload {
  return {
    ...plan,
    sections: plan.sections.map((section) => ({
      ...NEW_SECTION,
      ...section,
      ceiling_period: section.ceiling_period ?? "year",
      ceiling_period_years: section.ceiling_period_years ?? null,
      coverage_rate: section.coverage_rate ?? null,
      excluded_icd_codes: section.excluded_icd_codes ?? null,
      hospital_ids: section.hospital_ids ?? [],
    })),
  };
}

function toDraft(pkg: InsurancePackage): Draft {
  const { hospital_ids: _hospitals, members_count: _members, ...plan } = pkg;
  return structuredClone(withDefaults(plan));
}

function numberOrNull(value: string): number | null {
  return value.trim() === "" ? null : Number(value.replace(",", "."));
}

function numberInput(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

/**
 * Packages. Par défaut la liste (code, nom, statut, zone de couverture, assureur) ;
 * puis la création ou la modification : code unique dans le système, nom et plafond par personne obligatoires ;
 * sections selon les conditions assurantielles (plafond et périodicité, couverture %, affections non applicables,
 * prestataires) et, pour chaque code acte : plafond unitaire, périodicité, nombre maximum, validation préalable
 * (à partir d'un montant), âge minimum et maximum.
 */
export function ClaimsPackagesPage() {
  const { showToast } = useToast();
  const isAdmin = getStoredAccountType() === "admin";
  const [view, setView] = useState<"list" | "edit">("list");
  const [allPackages, setAllPackages] = useState<PackageSummary[]>([]);
  const [insurers, setInsurers] = useState<AdminInsurer[]>([]);
  const [insurerFilter, setInsurerFilter] = useState("");
  const [insurerId, setInsurerId] = useState<number | null>(getWorkContext().insurerId);
  const [meta, setMeta] = useState<PackagesMeta | null>(null);
  const [network, setNetwork] = useState<NetworkHospital[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [hospitalIds, setHospitalIds] = useState<number[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsInsurer, setNeedsInsurer] = useState(false);
  const [saving, setSaving] = useState(false);

  /** Catalogue de l'assureur actif (packages, codes actes, modèles) et son réseau. */
  const loadInsurer = useCallback(async (): Promise<PackagesMeta | null> => {
    try {
      const [data, hospitals] = await Promise.all([fetchClaimsPackages(), fetchClaimsNetwork()]);
      setMeta(data);
      setNetwork(hospitals);
      setNeedsInsurer(false);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Chargement impossible.";
      setNeedsInsurer(/Choisissez (un|l.)assureur/.test(message));
      if (!isAdmin) setError(message);
      setMeta(null);
      setNetwork([]);
      return null;
    }
  }, [isAdmin]);

  const loadList = useCallback(async () => {
    if (!isAdmin) {
      await loadInsurer();
      return;
    }
    try {
      const [rows, insurerRows] = await Promise.all([fetchAllPackages(), fetchAdminInsurers()]);
      setAllPackages(rows);
      setInsurers(insurerRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement des packages impossible.");
    }
  }, [isAdmin, loadInsurer]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const rows: ListRow[] = useMemo(() => {
    if (isAdmin) {
      return allPackages.filter((pkg) => !insurerFilter || pkg.insurer_id === Number(insurerFilter));
    }
    return (meta?.packages ?? []).map((pkg) => ({
      id: pkg.id,
      code: pkg.code,
      name: pkg.name,
      status: pkg.status,
      coverage_zone: pkg.coverage_zone,
      insurer_id: insurerId,
      insurer_name: "",
      acts_count: pkg.sections.reduce((n, s) => n + s.benefits.length, 0),
      members_count: pkg.members_count,
    }));
  }, [isAdmin, allPackages, insurerFilter, meta, insurerId]);

  const actNames = useMemo(() => new Map((meta?.act_codes ?? []).map((a) => [a.code, a.name])), [meta]);
  const insurerName = insurers.find((insurer) => insurer.id === insurerId)?.name ?? "";

  const select = (pkg: InsurancePackage | null) => {
    setError(null);
    setEditing(null);
    setPreview(false);
    setDraft(pkg ? toDraft(pkg) : EMPTY_DRAFT);
    setHospitalIds(pkg?.hospital_ids ?? []);
  };

  /** L'assureur du package devient l'assureur actif, pour charger son catalogue et son réseau. */
  const chooseInsurer = async (id: number | null): Promise<PackagesMeta | null> => {
    setInsurerId(id);
    if (!id) {
      setMeta(null);
      setNetwork([]);
      return null;
    }
    setWorkContext({ insurerId: id });
    return loadInsurer();
  };

  const openPackage = async (row: ListRow) => {
    setError(null);
    const data = isAdmin && row.insurer_id ? await chooseInsurer(row.insurer_id) : meta;
    const pkg = data?.packages.find((p) => p.id === row.id) ?? null;
    if (!pkg) {
      setError("Package introuvable chez cet assureur.");
      return;
    }
    select(pkg);
    setView("edit");
  };

  const openCreate = async () => {
    select(null);
    setView("edit");
    if (isAdmin && insurerId) await chooseInsurer(insurerId);
  };

  const backToList = async () => {
    setView("list");
    setError(null);
    await loadList();
  };

  const setSection = (index: number, patch: Partial<PackageSection>) =>
    setDraft((d) => ({ ...d, sections: d.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));

  const setBenefit = (sectionIndex: number, benefitIndex: number, patch: Partial<PackageBenefit>) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === sectionIndex ? { ...s, benefits: s.benefits.map((b, j) => (j === benefitIndex ? { ...b, ...patch } : b)) } : s,
      ),
    }));

  const moveSection = (index: number, delta: number) =>
    setDraft((d) => {
      const sections = [...d.sections];
      const target = index + delta;
      if (target < 0 || target >= sections.length) return d;
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return { ...d, sections };
    });

  /** Ajoute une ligne pour un code acte de la section (libellé = nom de l'acte). */
  const addActCode = (sectionIndex: number, code: string) => {
    const section = draft.sections[sectionIndex];
    if (!section || !code) return;
    setSection(sectionIndex, { benefits: [...section.benefits, { ...NEW_BENEFIT, label: actNames.get(code) ?? code, act_codes: [code] }] });
    setEditing(`${sectionIndex}-${section.benefits.length}`);
  };

  const applyTemplate = (key: string) => {
    const template = meta?.templates.find((t) => t.key === key);
    if (!template) return;
    setDraft({
      ...structuredClone(withDefaults(template.package)),
      id: draft.id,
      code: draft.code,
      name: draft.name || template.package.name,
    });
    setEditing(null);
    showToast(`Modèle « ${template.name} » appliqué : donnez-lui un code unique, vérifiez puis enregistrez.`);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { id, ...payload } = draft;
      const saved = await saveClaimsPackage(payload, id);
      const withHospitals = await setPackageHospitals(saved.id, hospitalIds);
      showToast("Package enregistré.");
      await loadInsurer();
      select(withHospitals);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin && needsInsurer) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Packages" }]} />
        <section className="theme-card p-6 text-center">
          <h1 className="text-lg font-semibold text-foreground">Choisissez d'abord un assureur</h1>
          <p className="mt-1 text-sm text-muted">
            Les packages appartiennent au catalogue d'un assureur. Sélectionnez l'assureur dans la liste en haut de page.
          </p>
        </section>
      </>
    );
  }

  if (view === "list") {
    return (
      <>
        <Breadcrumbs items={[{ label: "Packages" }]} />
        {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}
        <section className="theme-card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-foreground">Liste des packages</h1>
              <p className="mt-1 text-sm text-muted">{rows.length} package(s)</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isAdmin ? (
                <select value={insurerFilter} onChange={(e) => setInsurerFilter(e.target.value)} aria-label="Filtrer par assureur" className={`${selectClass} sm:w-56`}>
                  <option value="">Tous les assureurs</option>
                  {insurers.map((insurer) => (
                    <option key={insurer.id} value={insurer.id}>{insurer.name}</option>
                  ))}
                </select>
              ) : null}
              <button type="button" className={buttonClass} onClick={() => void openCreate()}>
                <Plus size={15} />
                Créer un package
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-border bg-card-muted/50 text-left text-[11px] uppercase tracking-wide text-muted">
                <tr>
                  {["#", "Code", "Nom", "Statut", "Zone de couverture", ...(isAdmin ? ["Assureur"] : []), "Actes", "Agents", ""].map((title) => (
                    <th key={title} className="px-4 py-3 font-semibold">{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 9 : 8} className="px-4 py-8 text-center text-muted">Aucun package.</td>
                  </tr>
                ) : null}
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                    <td className="px-4 py-3 text-muted">{index + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-primary">{row.code}</td>
                    <td className="px-4 py-3 font-semibold text-foreground">{row.name}</td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={row.status === "active" ? "success" : "neutral"}>{row.status === "active" ? "ACTIF" : "INACTIF"}</StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-foreground">{row.coverage_zone || "—"}</td>
                    {isAdmin ? <td className="px-4 py-3 text-foreground">{row.insurer_name}</td> : null}
                    <td className="px-4 py-3 tabular-nums text-foreground">{row.acts_count}</td>
                    <td className="px-4 py-3 tabular-nums text-foreground">{row.members_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => void openPackage(row)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded border border-teal/50 text-teal hover:bg-teal-soft"
                        title="Voir"
                        aria-label={`Voir ${row.name}`}
                      >
                        <Eye size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  }

  const canSave = Boolean(meta) && draft.code.trim() !== "" && draft.name.trim() !== "" && (draft.person_ceiling ?? 0) > 0;

  return (
    <>
      <Breadcrumbs items={[{ label: "Packages" }, { label: draft.id ? draft.name : "Créer un package" }]} />
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <section className="theme-card flex flex-col gap-6 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg font-semibold text-foreground">{draft.id ? `Plan ${draft.name}` : "Nouveau package"}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {meta && (meta.templates ?? []).length > 0 ? (
              <select aria-label="Partir d'un modèle" value="" onChange={(e) => applyTemplate(e.target.value)} className={`${selectClass} h-9 w-auto`}>
                <option value="">Partir d'un modèle…</option>
                {meta.templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            ) : null}
            <button type="button" className={ghostClass} onClick={() => setPreview((p) => !p)}>
              {preview ? <Pencil size={14} /> : <Eye size={14} />}
              {preview ? "Modifier" : "Aperçu du plan"}
            </button>
            <button type="button" className={ghostClass} onClick={() => void backToList()}>
              <ArrowLeft size={14} />
              Liste des packages
            </button>
          </div>
        </div>

        {isAdmin ? (
          <div className="max-w-md">
            <FormField label="Assureur" required hint={draft.id ? "Un package reste chez l'assureur qui l'a créé." : undefined}>
              <select
                value={insurerId ?? ""}
                disabled={Boolean(draft.id)}
                onChange={(e) => void chooseInsurer(e.target.value ? Number(e.target.value) : null)}
                className={selectClass}
              >
                <option value="">Choisir l'assureur</option>
                {insurers.map((insurer) => (
                  <option key={insurer.id} value={insurer.id}>{insurer.name}</option>
                ))}
              </select>
            </FormField>
          </div>
        ) : null}

        {!meta ? (
          <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted">Choisissez l'assureur du package pour le configurer.</p>
        ) : preview ? (
          <BenefitPlanTable plan={draft} showCodes />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <FormField label="Code" required hint={draft.id ? "Figé après l'enregistrement." : "Unique dans tout le système ; figé après l'enregistrement."}>
                <input value={draft.code} disabled={Boolean(draft.id)} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className={inputClass} placeholder="ACTIVA-CONFORT" />
              </FormField>
              <FormField label="Nom" required hint={draft.id ? "Figé après l'enregistrement." : undefined}>
                <input value={draft.name} disabled={Boolean(draft.id)} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputClass} placeholder="Confort" />
              </FormField>
              <FormField label="Plafond par personne / an" required>
                <input type="number" min={0} value={numberInput(draft.person_ceiling)} onChange={(e) => setDraft({ ...draft, person_ceiling: numberOrNull(e.target.value) })} className={inputClass} placeholder="15000" />
              </FormField>
              <FormField label="Plafond par famille / an">
                <input type="number" min={0} value={numberInput(draft.family_ceiling)} onChange={(e) => setDraft({ ...draft, family_ceiling: numberOrNull(e.target.value) })} className={inputClass} placeholder="30000" />
              </FormField>
              <FormField label="Zone de couverture">
                <input value={draft.coverage_zone ?? ""} onChange={(e) => setDraft({ ...draft, coverage_zone: e.target.value || null })} className={inputClass} placeholder="RDC" />
              </FormField>
              <FormField label="Statut">
                <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as Draft["status"] })} className={selectClass}>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </FormField>
              <FormField label="Devise">
                <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className={selectClass}>
                  <option value="USD">USD</option>
                  <option value="CDF">CDF</option>
                </select>
              </FormField>
              <FormField label="Description">
                <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className={inputClass} />
              </FormField>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Sections</h3>
                  <p className="text-xs text-muted">
                    Les sections reprennent les options des conditions assurantielles. Un acte qui ne figure dans aucune section n'est
                    pas pris en charge (« non prévu au plan »).
                  </p>
                </div>
                <button type="button" className={ghostClass} onClick={() => setDraft({ ...draft, sections: [...draft.sections, { ...NEW_SECTION }] })}>
                  <Plus size={14} />
                  Ajouter une section
                </button>
              </div>

              {draft.sections.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted">
                  Aucune section. Ajoutez-en une (ex. Consultation générale), ou partez d'un modèle. Sans section, tous les actes sont pris en charge à 100 %.
                </p>
              ) : null}

              {draft.sections.map((section, sectionIndex) => (
                <SectionEditor
                  key={sectionIndex}
                  section={section}
                  sectionIndex={sectionIndex}
                  currency={draft.currency}
                  meta={meta}
                  actNames={actNames}
                  network={network}
                  editing={editing}
                  setEditing={setEditing}
                  onChange={(patch) => setSection(sectionIndex, patch)}
                  onBenefit={(benefitIndex, patch) => setBenefit(sectionIndex, benefitIndex, patch)}
                  onAddCode={(code) => addActCode(sectionIndex, code)}
                  onMove={(delta) => moveSection(sectionIndex, delta)}
                  onRemove={() => setDraft({ ...draft, sections: draft.sections.filter((_, i) => i !== sectionIndex) })}
                />
              ))}
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Prestataires qui acceptent ce package</h3>
              {network.length === 0 ? <p className="text-sm text-muted">Aucun prestataire dans le réseau de {insurerName || "l'assureur"}.</p> : null}
              <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                {network.map((hospital) => (
                  <label key={hospital.hospital_id} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={hospitalIds.includes(hospital.hospital_id)}
                      onChange={(e) =>
                        setHospitalIds((current) => (e.target.checked ? [...current, hospital.hospital_id] : current.filter((id) => id !== hospital.hospital_id)))
                      }
                    />
                    {hospital.name}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {draft.id ? (
            <span className="mr-auto inline-flex items-center gap-1 text-xs text-muted">
              <FileText size={13} />
              Les factures déjà codées gardent leur prise en charge ; elle est recalculée à la prochaine codification.
            </span>
          ) : null}
          {!canSave && meta ? <span className="text-xs text-muted">Code, nom et plafond par personne sont obligatoires.</span> : null}
          <button type="button" className={buttonClass} disabled={saving || !canSave} onClick={() => void handleSave()}>
            <Save size={15} />
            Enregistrer le package
          </button>
        </div>
      </section>
    </>
  );
}

type SectionProps = {
  section: PackageSection;
  sectionIndex: number;
  currency: string;
  meta: PackagesMeta;
  actNames: Map<string, string>;
  network: NetworkHospital[];
  editing: string | null;
  setEditing: (key: string | null) => void;
  onChange: (patch: Partial<PackageSection>) => void;
  onBenefit: (benefitIndex: number, patch: Partial<PackageBenefit>) => void;
  onAddCode: (code: string) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
};

/** Une section : plafond et périodicité, couverture, affections non applicables, prestataires, puis ses codes actes. */
function SectionEditor({ section, sectionIndex, currency, meta, actNames, network, editing, setEditing, onChange, onBenefit, onAddCode, onMove, onRemove }: SectionProps) {
  const used = new Set(section.benefits.flatMap((b) => b.act_codes));
  const available = meta.act_codes.filter((a) => !used.has(a.code));

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-1 items-end gap-2 border-b border-border bg-card-muted/50 p-3 md:grid-cols-12">
        <FormField label="Section" required className="md:col-span-4">
          <input value={section.name} onChange={(e) => onChange({ name: e.target.value })} className={inputClass} placeholder="Consultation générale" />
        </FormField>
        <FormField label="Plafond" className="md:col-span-2">
          <input type="number" min={0} value={numberInput(section.ceiling_amount)} onChange={(e) => onChange({ ceiling_amount: numberOrNull(e.target.value) })} className={inputClass} />
        </FormField>
        <FormField label="Périodicité" className="md:col-span-2">
          <select value={section.ceiling_period} onChange={(e) => onChange({ ceiling_period: e.target.value as PackageSection["ceiling_period"] })} className={selectClass}>
            {meta.periods.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Années" className="md:col-span-1">
          <input
            type="number"
            min={2}
            max={10}
            disabled={section.ceiling_period !== "years"}
            value={section.ceiling_period === "years" ? section.ceiling_period_years ?? 2 : ""}
            onChange={(e) => onChange({ ceiling_period_years: Number(e.target.value) })}
            className={inputClass}
          />
        </FormField>
        <FormField label="Couverture (%)" className="md:col-span-2">
          <input type="number" min={0} max={100} value={numberInput(section.coverage_rate)} onChange={(e) => onChange({ coverage_rate: numberOrNull(e.target.value) })} className={inputClass} placeholder="100" />
        </FormField>
        <div className="flex justify-end gap-1 md:col-span-1">
          <button type="button" aria-label="Monter la section" className="rounded p-1.5 text-muted hover:bg-accent/20" onClick={() => onMove(-1)}>
            <ChevronUp size={15} />
          </button>
          <button type="button" aria-label="Descendre la section" className="rounded p-1.5 text-muted hover:bg-accent/20" onClick={() => onMove(1)}>
            <ChevronDown size={15} />
          </button>
          <button type="button" aria-label="Supprimer la section" className="rounded p-1.5 text-muted hover:bg-card-muted hover:text-red-600" onClick={onRemove}>
            <Trash2 size={15} />
          </button>
        </div>
        <FormField label="Codes d'affection non applicables" hint="Codes ou plages CIM-10 : C00-D48, F10" className="md:col-span-4">
          <input value={section.excluded_icd_codes ?? ""} onChange={(e) => onChange({ excluded_icd_codes: e.target.value.toUpperCase() || null })} className={inputClass} />
        </FormField>
        <FormField label="Carence (mois)" className="md:col-span-2">
          <input type="number" min={0} value={numberInput(section.waiting_months)} onChange={(e) => onChange({ waiting_months: numberOrNull(e.target.value) })} className={inputClass} />
        </FormField>
        <FormField label="Mention" className="md:col-span-6">
          <input value={section.note ?? ""} onChange={(e) => onChange({ note: e.target.value || null })} className={inputClass} />
        </FormField>
        <div className="md:col-span-12">
          <span className="mb-1 block text-xs font-medium uppercase text-muted">Prestataires de la section (aucun coché = tous ceux du package)</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {network.length === 0 ? <span className="text-xs text-muted">Aucun prestataire dans le réseau.</span> : null}
            {network.map((hospital) => (
              <label key={hospital.hospital_id} className="flex items-center gap-2 text-xs text-foreground">
                <input
                  type="checkbox"
                  checked={section.hospital_ids.includes(hospital.hospital_id)}
                  onChange={(e) =>
                    onChange({
                      hospital_ids: e.target.checked
                        ? [...section.hospital_ids, hospital.hospital_id]
                        : section.hospital_ids.filter((id) => id !== hospital.hospital_id),
                    })
                  }
                />
                {hospital.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      <ul className="divide-y divide-border">
        {section.benefits.map((benefit, benefitIndex) => {
          const key = `${sectionIndex}-${benefitIndex}`;
          const rate = effectiveRate(benefit, section.coverage_rate);
          return (
            <li key={key} className="p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {benefit.act_codes.length > 0 ? <span className="mr-2 font-mono text-xs text-primary">{benefit.act_codes.join(", ")}</span> : null}
                    {benefit.label || <span className="text-muted">Nouvel acte</span>}
                  </p>
                  <p className="text-xs text-muted">
                    <span className={rate <= 0 ? "font-semibold text-red-700" : "font-semibold text-foreground"}>{rate}%</span>
                    {benefit.coverage_rate === null ? " (section)" : ""} · {benefitCeilingText(benefit, currency, rate)}
                    {benefit.icd_codes ? ` · CIM-10 ${benefit.icd_codes}` : ""}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button type="button" className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-accent/20" onClick={() => setEditing(editing === key ? null : key)}>
                    {editing === key ? <X size={13} /> : <Pencil size={13} />}
                    {editing === key ? "Fermer" : "Modifier"}
                  </button>
                  <button
                    type="button"
                    aria-label="Supprimer l'acte"
                    className="rounded p-1 text-muted hover:bg-card-muted hover:text-red-600"
                    onClick={() => {
                      setEditing(null);
                      onChange({ benefits: section.benefits.filter((_, j) => j !== benefitIndex) });
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {editing === key ? <BenefitEditor benefit={benefit} meta={meta} actNames={actNames} onChange={(patch) => onBenefit(benefitIndex, patch)} /> : null}
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <select aria-label="Ajouter un code acte à la section" value="" onChange={(e) => onAddCode(e.target.value)} className={`${selectClass} h-9 w-auto max-w-sm text-sm`}>
          <option value="">+ Ajouter un code acte…</option>
          {available.map((a) => (
            <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          onClick={() => {
            onChange({ benefits: [...section.benefits, { ...NEW_BENEFIT }] });
            setEditing(`${sectionIndex}-${section.benefits.length}`);
          }}
        >
          <Plus size={14} />
          Ligne par affection ou mention
        </button>
      </div>
    </div>
  );
}

type EditorProps = {
  benefit: PackageBenefit;
  meta: PackagesMeta;
  actNames: Map<string, string>;
  onChange: (patch: Partial<PackageBenefit>) => void;
};

/** Réglages d'un code acte : plafond unitaire, périodicité, nombre max, validation préalable, âges. */
function BenefitEditor({ benefit, meta, actNames, onChange }: EditorProps) {
  const available = meta.act_codes.filter((a) => !benefit.act_codes.includes(a.code));

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg bg-card-muted/40 p-3 md:grid-cols-6">
      <FormField label="Libellé" required className="md:col-span-3">
        <input value={benefit.label} onChange={(e) => onChange({ label: e.target.value })} className={inputClass} placeholder="Médecine générale" />
      </FormField>
      <FormField label="Couverture (%)" hint="Vide = celle de la section" className="md:col-span-1">
        <input type="number" min={0} max={100} value={numberInput(benefit.coverage_rate)} onChange={(e) => onChange({ coverage_rate: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>
      <FormField label="Mention" className="md:col-span-2">
        <input value={benefit.note ?? ""} onChange={(e) => onChange({ note: e.target.value || null })} className={inputClass} placeholder="Frais réel" />
      </FormField>

      <div className="md:col-span-4">
        <span className="mb-1 block text-xs font-medium uppercase text-muted">Code(s) acte</span>
        <div className="flex flex-wrap items-center gap-1">
          {benefit.act_codes.map((code) => (
            <span key={code} title={actNames.get(code)} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
              {code}
              <button type="button" aria-label={`Retirer ${code}`} onClick={() => onChange({ act_codes: benefit.act_codes.filter((c) => c !== code) })}>
                <X size={11} />
              </button>
            </span>
          ))}
          <select
            aria-label="Ajouter un code acte"
            value=""
            onChange={(e) => e.target.value && onChange({ act_codes: [...benefit.act_codes, e.target.value] })}
            className={`${selectClass} h-8 w-auto max-w-xs text-xs`}
          >
            <option value="">+ Code…</option>
            {available.map((a) => (
              <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
            ))}
          </select>
        </div>
      </div>
      <FormField label="Affections CIM-10 visées" hint="ex. Z34-Z36" className="md:col-span-2">
        <input value={benefit.icd_codes ?? ""} onChange={(e) => onChange({ icd_codes: e.target.value.toUpperCase() || null })} className={inputClass} />
      </FormField>

      <FormField label="Plafond unitaire">
        <input type="number" min={0} value={numberInput(benefit.unit_ceiling)} onChange={(e) => onChange({ unit_ceiling: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>
      <FormField label="Par">
        <select value={benefit.unit_basis} onChange={(e) => onChange({ unit_basis: e.target.value as PackageBenefit["unit_basis"] })} className={selectClass}>
          <option value="act">Acte</option>
          <option value="day">Jour</option>
        </select>
      </FormField>
      <FormField label="Périodicité">
        <select value={benefit.period} onChange={(e) => onChange({ period: e.target.value as PackageBenefit["period"] })} className={selectClass}>
          {meta.periods.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Années">
        <input
          type="number"
          min={2}
          max={10}
          disabled={benefit.period !== "years"}
          value={benefit.period === "years" ? benefit.period_years ?? 2 : ""}
          onChange={(e) => onChange({ period_years: Number(e.target.value) })}
          className={inputClass}
        />
      </FormField>
      <FormField label="Nombre max">
        <input type="number" min={1} value={numberInput(benefit.max_quantity)} onChange={(e) => onChange({ max_quantity: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>
      <FormField label="Plafond sur la période">
        <input type="number" min={0} value={numberInput(benefit.period_ceiling)} onChange={(e) => onChange({ period_ceiling: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>

      <FormField label="Validation préalable" className="md:col-span-2">
        <select value={benefit.prior_approval} onChange={(e) => onChange({ prior_approval: e.target.value as PackageBenefit["prior_approval"] })} className={selectClass}>
          {meta.prior_approvals.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </FormField>
      <FormField label="À partir de (montant)">
        <input
          type="number"
          min={0}
          disabled={benefit.prior_approval !== "above"}
          value={numberInput(benefit.prior_approval_threshold)}
          onChange={(e) => onChange({ prior_approval_threshold: numberOrNull(e.target.value) })}
          className={inputClass}
        />
      </FormField>
      <FormField label="Âge min">
        <input type="number" min={0} value={numberInput(benefit.age_min)} onChange={(e) => onChange({ age_min: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>
      <FormField label="Âge max">
        <input type="number" min={0} value={numberInput(benefit.age_max)} onChange={(e) => onChange({ age_max: numberOrNull(e.target.value) })} className={inputClass} />
      </FormField>
    </div>
  );
}
